import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, Calendar, Tag, Share2 } from 'lucide-react';
import BackgroundAnimation from '../components/BackgroundAnimation';
import SEOHead from '../components/SEOHead';
import blogArticles from '../data/blogArticles';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const article = blogArticles.find(a => a.slug === slug);
  const currentIndex = blogArticles.findIndex(a => a.slug === slug);
  const prevArticle = currentIndex > 0 ? blogArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < blogArticles.length - 1 ? blogArticles[currentIndex + 1] : null;

  useEffect(() => {
    if (!article) {
      navigate('/blog');
    }
  }, [article, navigate]);

  if (!article) return null;

  const articleSchema = {
    "@type": "BlogPosting",
    "@id": `https://americanprimellc.com/blog/${article.slug}#article`,
    "mainEntityOfPage": `https://americanprimellc.com/blog/${article.slug}`,
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "datePublished": article.publishedDate,
    "dateModified": article.publishedDate,
    "author": {
      "@type": "Organization",
      "name": "American Prime LLC",
      "url": "https://americanprimellc.com"
    },
    "publisher": {
      "@id": "https://americanprimellc.com/#organization"
    },
    "articleSection": article.category,
    "wordCount": article.content.split(' ').length
  };

  // Simple markdown-like rendering
  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let currentList = [];
    let listType = null;

    const flushList = () => {
      if (currentList.length > 0) {
        if (listType === 'ul') {
          elements.push(
            <ul key={`list-${elements.length}`} className="list-disc pl-6 mb-6 space-y-2" style={{ color: 'var(--text-secondary)' }}>
              {currentList.map((item, i) => <li key={i} className="body-medium">{item}</li>)}
            </ul>
          );
        }
        currentList = [];
        listType = null;
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      if (!trimmedLine) {
        flushList();
        return;
      }

      // Headers
      if (trimmedLine.startsWith('# ')) {
        flushList();
        elements.push(
          <h1 key={index} className="display-large mb-6 mt-8" style={{ color: 'var(--text-primary)' }}>
            {trimmedLine.slice(2)}
          </h1>
        );
      } else if (trimmedLine.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={index} className="heading-1 mb-4 mt-10" style={{ color: 'var(--text-primary)' }}>
            {trimmedLine.slice(3)}
          </h2>
        );
      } else if (trimmedLine.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={index} className="heading-2 mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>
            {trimmedLine.slice(4)}
          </h3>
        );
      } else if (trimmedLine.startsWith('**Q:')) {
        flushList();
        elements.push(
          <p key={index} className="body-medium font-semibold mb-2 mt-6" style={{ color: 'var(--text-primary)' }}>
            {trimmedLine.replace(/\*\*/g, '')}
          </p>
        );
      } else if (trimmedLine.startsWith('**A:') || trimmedLine.startsWith('A:')) {
        elements.push(
          <p key={index} className="body-medium mb-4 pl-4" style={{ color: 'var(--text-secondary)', borderLeft: '2px solid var(--brand-primary)' }}>
            {trimmedLine.replace(/\*\*/g, '').replace('A:', '').trim()}
          </p>
        );
      } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
        listType = 'ul';
        currentList.push(trimmedLine.slice(2));
      } else if (trimmedLine.startsWith('|')) {
        // Skip table rows for simplicity
        flushList();
      } else if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
        flushList();
        elements.push(
          <p key={index} className="body-large font-semibold mb-4 mt-6" style={{ color: 'var(--text-primary)' }}>
            {trimmedLine.slice(2, -2)}
          </p>
        );
      } else if (trimmedLine.includes('[') && trimmedLine.includes('](')) {
        flushList();
        // Parse links
        const linkMatch = trimmedLine.match(/\[([^\]]+)\]\(([^)]+)\)/);
        if (linkMatch) {
          const beforeLink = trimmedLine.split('[')[0];
          const afterLink = trimmedLine.split(')').slice(1).join(')');
          elements.push(
            <p key={index} className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
              {beforeLink}
              <a 
                href={linkMatch[2]} 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-medium hover:underline"
                style={{ color: 'var(--brand-primary)' }}
              >
                {linkMatch[1]}
              </a>
              {afterLink}
            </p>
          );
        }
      } else {
        flushList();
        // Regular paragraph - handle inline bold
        const text = trimmedLine.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        elements.push(
          <p 
            key={index} 
            className="body-medium mb-4" 
            style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        );
      }
    });

    flushList();
    return elements;
  };

  return (
    <div className="min-h-screen page-transition" style={{ background: 'var(--bg-primary)', position: 'relative' }}>
      <SEOHead 
        title={article.title}
        description={article.excerpt}
        canonicalPath={`/blog/${article.slug}`}
        schema={articleSchema}
      />
      <BackgroundAnimation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-8" style={{ padding: '120px 7.6923% 40px' }}>
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 mb-8 hover:opacity-70 transition-opacity"
            style={{ color: 'var(--brand-primary)' }}
          >
            <ArrowLeft size={18} />
            <span>Back to Blog</span>
          </Link>
          
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span 
              className="px-3 py-1 rounded-full text-sm"
              style={{ background: 'rgba(0, 255, 209, 0.1)', color: 'var(--brand-primary)' }}
            >
              {article.category}
            </span>
            <span className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
              <Clock size={14} />
              {article.readTime}
            </span>
            <span className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
              <Calendar size={14} />
              {new Date(article.publishedDate).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>
          
          <h1 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>
            {article.title}
          </h1>
          
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            {article.excerpt}
          </p>
        </div>
      </section>

      {/* Featured Image */}
      <section style={{ padding: '0 7.6923%' }}>
        <div className="max-w-4xl mx-auto mb-12">
          <img 
            src={article.image} 
            alt={article.title}
            className="w-full h-64 md:h-96 object-cover rounded-lg"
          />
        </div>
      </section>

      {/* Article Content */}
      <section style={{ padding: '0 7.6923%' }}>
        <article className="max-w-4xl mx-auto prose-lg">
          {renderContent(article.content)}
        </article>
      </section>

      {/* Mid-Article CTA */}
      <section className="py-12" style={{ padding: '60px 7.6923%' }}>
        <div 
          className="max-w-4xl mx-auto p-8 rounded-lg text-center"
          style={{ 
            background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.1) 0%, rgba(0, 255, 209, 0.05) 100%)',
            border: '1px solid rgba(0, 255, 209, 0.2)'
          }}
        >
          <h3 className="heading-2 mb-4" style={{ color: 'var(--text-primary)' }}>
            Ready to Get Started?
          </h3>
          <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
            Book a free strategy call to discuss how AI automation can transform your business.
          </p>
          <a
            href="https://calendly.com/americanprimeforyou"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 font-medium transition-all hover:scale-105"
            style={{
              background: 'var(--brand-primary)',
              color: '#000000',
              textDecoration: 'none'
            }}
            onClick={() => window.trackCTA && window.trackCTA('Book Strategy Call', 'blog_mid_cta')}
          >
            Book Free Strategy Call
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12" style={{ padding: '60px 7.6923%' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            {prevArticle ? (
              <Link 
                to={`/blog/${prevArticle.slug}`}
                className="flex-1 p-4 rounded-lg group"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.03)', 
                  border: '1px solid var(--border-subtle)' 
                }}
              >
                <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--text-muted)' }}>
                  <ArrowLeft size={14} />
                  <span className="text-sm">Previous Article</span>
                </div>
                <p className="font-medium group-hover:text-[var(--brand-primary)] transition-colors line-clamp-2" style={{ color: 'var(--text-primary)' }}>
                  {prevArticle.title}
                </p>
              </Link>
            ) : <div className="flex-1" />}
            
            {nextArticle && (
              <Link 
                to={`/blog/${nextArticle.slug}`}
                className="flex-1 p-4 rounded-lg text-right group"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.03)', 
                  border: '1px solid var(--border-subtle)' 
                }}
              >
                <div className="flex items-center justify-end gap-2 mb-2" style={{ color: 'var(--text-muted)' }}>
                  <span className="text-sm">Next Article</span>
                  <ArrowRight size={14} />
                </div>
                <p className="font-medium group-hover:text-[var(--brand-primary)] transition-colors line-clamp-2" style={{ color: 'var(--text-primary)' }}>
                  {nextArticle.title}
                </p>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Related Articles */}
      <section className="py-16" style={{ padding: '80px 7.6923%', background: 'var(--bg-secondary)' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="heading-1 mb-8 text-center" style={{ color: 'var(--text-primary)' }}>
            More Articles
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {blogArticles
              .filter(a => a.id !== article.id)
              .slice(0, 3)
              .map((relatedArticle) => (
                <Link 
                  key={relatedArticle.id}
                  to={`/blog/${relatedArticle.slug}`}
                  className="block rounded-lg overflow-hidden group"
                  style={{ 
                    background: 'var(--bg-primary)', 
                    border: '1px solid var(--border-subtle)' 
                  }}
                >
                  <div className="h-40 overflow-hidden">
                    <img 
                      src={relatedArticle.image} 
                      alt={relatedArticle.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="heading-3 mb-2 line-clamp-2 group-hover:text-[var(--brand-primary)] transition-colors" style={{ color: 'var(--text-primary)' }}>
                      {relatedArticle.title}
                    </h3>
                    <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                      {relatedArticle.readTime}
                    </span>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
